import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { 
  Clock, 
  ChevronLeft, 
  ChevronRight, 
  Search,
  Download,
  FolderOpen,
  CheckSquare,
  User,
  Umbrella,
  ChevronDown,
  FileText,
  BarChart3,
  TrendingUp,
  AlertTriangle,
  X,
  CalendarDays
} from 'lucide-react';
import { format, addWeeks, subWeeks, addMonths, subMonths, startOfWeek, endOfWeek, startOfMonth, endOfMonth, eachDayOfInterval, parseISO, isToday } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toUTCPlus5Time, toPKTDayKey, formatPKTDayLabel, parseApiDate } from '../utils/time';
import { useSystemSettings } from '../hooks/api';
import { SYSTEM_SETTINGS_DEFAULTS } from '../constants/systemSettingsDefaults';
import { ViewTabs } from './ui';

const VIEW_TABS = [
  { id: 'week', label: 'Week' },
  { id: 'month', label: 'Month' },
];

const Timesheet = ({ lockedUserId = null } = {}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: systemSettings } = useSystemSettings();
  const weeklyTarget = systemSettings?.weekly_target_hours ?? SYSTEM_SETTINGS_DEFAULTS.weekly_target_hours;
  const dailyTarget = systemSettings?.daily_target_hours ?? SYSTEM_SETTINGS_DEFAULTS.daily_target_hours;
  const holidayDateSet = useMemo(() => {
    const list = systemSettings?.company_holidays || SYSTEM_SETTINGS_DEFAULTS.company_holidays || [];
    return new Set(
      (Array.isArray(list) ? list : [])
        .map((h) => (typeof h === 'string' ? h : h?.date))
        .filter(Boolean)
        .map((d) => String(d).slice(0, 10))
    );
  }, [systemSettings?.company_holidays]);
  const holidayNameByDate = useMemo(() => {
    const list = systemSettings?.company_holidays || [];
    const map = {};
    (Array.isArray(list) ? list : []).forEach((h) => {
      const date = String(typeof h === 'string' ? h : h?.date || '').slice(0, 10);
      if (!date) return;
      map[date] = (typeof h === 'object' && h?.name) ? String(h.name) : 'Holiday';
    });
    return map;
  }, [systemSettings?.company_holidays]);
  const isUserLocked = Boolean(lockedUserId);
  const canFilterEmployees = !isUserLocked && (user?.role === 'admin' || user?.role === 'project_lead');
  const [timeEntries, setTimeEntries] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(
    lockedUserId || (user?.role === 'member' ? user.id : '')
  );
  const [viewMode, setViewMode] = useState('week'); // 'week' | 'month'
  const [periodAnchor, setPeriodAnchor] = useState(() => startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [loading, setLoading] = useState(true);
  const [projectFilter, setProjectFilter] = useState('');
  const [taskFilter, setTaskFilter] = useState('');
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [leaveData, setLeaveData] = useState({});
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(5);
  const [openFilterDropdown, setOpenFilterDropdown] = useState(null);
  const [employeeDropdownSearch, setEmployeeDropdownSearch] = useState('');
  const [projectDropdownSearch, setProjectDropdownSearch] = useState('');
  const [taskDropdownSearch, setTaskDropdownSearch] = useState('');
  const [expandedCells, setExpandedCells] = useState({});

  const periodStart = useMemo(
    () => (viewMode === 'month'
      ? startOfMonth(periodAnchor)
      : startOfWeek(periodAnchor, { weekStartsOn: 1 })),
    [viewMode, periodAnchor]
  );
  const periodEnd = useMemo(
    () => (viewMode === 'month'
      ? endOfMonth(periodAnchor)
      : endOfWeek(periodAnchor, { weekStartsOn: 1 })),
    [viewMode, periodAnchor]
  );
  const days = useMemo(
    () => eachDayOfInterval({ start: periodStart, end: periodEnd }),
    [periodStart, periodEnd]
  );
  const periodLabel = viewMode === 'month' ? 'month' : 'week';

  useEffect(() => {
    if (lockedUserId) setSelectedUser(lockedUserId);
  }, [lockedUserId]);

  const fetchTimesheetData = useCallback(async () => {
    setLoading(true);
    try {
      const startDate = format(periodStart, 'yyyy-MM-dd');
      const endDate = format(periodEnd, 'yyyy-MM-dd');
      const params = new URLSearchParams({
        start_date: startDate,
        end_date: endDate,
      });
      if (lockedUserId) params.set('user_id', lockedUserId);
      const response = await axios.get(`/api/timesheets?${params.toString()}`);
      setTimeEntries(response.data.time_entries || []);
      if (response.data.users?.length) {
        setUsers(response.data.users);
      }
    } catch (error) {
      console.error('Timesheet fetch error:', error);
      if (error.response?.status === 500) {
        toast.error('Server error - please try again later');
      } else {
        toast.error('Failed to fetch timesheet data');
      }
      setTimeEntries([]);
    } finally {
      setLoading(false);
    }
  }, [periodStart, periodEnd, lockedUserId]);

  const fetchAllUsers = useCallback(async () => {
    if (lockedUserId) return;
    try {
      const response = await axios.get('/api/team/active');
      const memberUsers = response.data?.filter(u => {
        const eligible =
          (u.role === 'member' || u.role === 'project_lead' || u.role === 'admin') &&
          (u.full_name?.trim() || u.username?.trim());
        if (!eligible) return false;
        if (user?.role === 'project_lead') {
          const leadName = (user.full_name || '').trim().toLowerCase();
          const isSelf = u.id === user.id;
          const isReportee = (u.team_lead || '').trim().toLowerCase() === leadName;
          return isSelf || isReportee;
        }
        return true;
      }) || [];
      const sortedUsers = memberUsers.sort((a, b) => {
        const nameA = (a.full_name || a.username || '').toLowerCase().trim();
        const nameB = (b.full_name || b.username || '').toLowerCase().trim();
        return nameA.localeCompare(nameB);
      });
      setUsers(sortedUsers);
    } catch (error) {
      console.error('Failed to fetch all users:', error);
    }
  }, [lockedUserId, user?.id, user?.role, user?.full_name]);

  const fetchProjects = useCallback(async () => {
    try {
      const response = await axios.get('/api/projects?page_size=100');
      setProjects(response.data.results || []);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    }
  }, []);

  const fetchTasks = useCallback(async () => {
    try {
      const response = await axios.get('/api/tasks');
      setTasks(response.data.results || []);
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
    }
  }, []);

  const fetchLeaveData = useCallback(async () => {
    if (!selectedUser) return;
    try {
      const startDate = format(periodStart, 'yyyy-MM-dd');
      const endDate = format(periodEnd, 'yyyy-MM-dd');
      const params = new URLSearchParams({
        start_date: startDate,
        end_date: endDate,
      });
      // Omit user_id for self — endpoint defaults to current user and avoids 403 for non-admins
      if (selectedUser !== user?.id) {
        params.set('user_id', selectedUser);
      }
      const response = await axios.get(`/api/leaves/leave-days?${params.toString()}`);
      setLeaveData(response.data?.days || {});
    } catch (error) {
      console.error('Failed to fetch leave data:', error);
      setLeaveData({});
    }
  }, [selectedUser, periodStart, periodEnd, user?.id]);

  useEffect(() => {
    fetchTimesheetData();
    if (canFilterEmployees) fetchAllUsers();
    fetchProjects();
    fetchTasks();
  }, [periodStart, periodEnd, canFilterEmployees, fetchTimesheetData, fetchAllUsers, fetchProjects, fetchTasks]);

  useEffect(() => { fetchLeaveData(); }, [selectedUser, periodStart, periodEnd, fetchLeaveData]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showExportDropdown && !event.target.closest('.export-dropdown')) setShowExportDropdown(false);
      if (openFilterDropdown && !event.target.closest('.filter-dropdown')) {
        setOpenFilterDropdown(null);
        setEmployeeDropdownSearch('');
        setProjectDropdownSearch('');
        setTaskDropdownSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showExportDropdown, openFilterDropdown]);

  const handleViewModeChange = (next) => {
    setViewMode(next);
    setPeriodAnchor((prev) => (
      next === 'month'
        ? startOfMonth(prev)
        : startOfWeek(prev, { weekStartsOn: 1 })
    ));
  };

  const goToPreviousPeriod = () => setPeriodAnchor(
    viewMode === 'month' ? subMonths(periodAnchor, 1) : subWeeks(periodAnchor, 1)
  );
  const goToNextPeriod = () => setPeriodAnchor(
    viewMode === 'month' ? addMonths(periodAnchor, 1) : addWeeks(periodAnchor, 1)
  );
  const jumpToDate = (dateStr) => {
    if (!dateStr) return;
    const date = parseISO(dateStr);
    setPeriodAnchor(
      viewMode === 'month'
        ? startOfMonth(date)
        : startOfWeek(date, { weekStartsOn: 1 })
    );
  };
  const goToToday = () => setPeriodAnchor(
    viewMode === 'month'
      ? startOfMonth(new Date())
      : startOfWeek(new Date(), { weekStartsOn: 1 })
  );
  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  // --- Export helpers ---
  const decodeExportText = (value) => {
    if (value == null) return '';
    return String(value)
      .replace(/&amp;/gi, '&')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&quot;/gi, '"')
      .replace(/&#39;|&apos;/gi, "'")
      .replace(/\r\n|\r|\n/g, ' ')
      .trim();
  };

  const csvCell = (value) => `"${decodeExportText(value).replace(/"/g, '""')}"`;
  const csvRow = (cells) => cells.map(csvCell).join(',');

  const formatHoursHhMm = (seconds) => {
    const total = Math.max(0, Math.round(Number(seconds) || 0));
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    return `${hours}:${String(minutes).padStart(2, '0')}`;
  };

  const idleMinutesForEntry = (entry) => {
    const periods = Array.isArray(entry?.idle_periods) ? entry.idle_periods : [];
    const seconds = periods.reduce((sum, p) => {
      if (typeof p === 'number') return sum + p;
      if (p && typeof p.duration === 'number') return sum + p.duration;
      if (p && typeof p.seconds === 'number') return sum + p.seconds;
      if (p && typeof p.start === 'number' && typeof p.end === 'number') return sum + Math.max(0, p.end - p.start);
      return sum;
    }, 0);
    return Math.round(seconds / 60);
  };

  const resolveUserMeta = (entry) => {
    const fromUsers = users.find((u) => u.id === entry?.user_id) || {};
    return {
      name: entry?.user_name || fromUsers.full_name || fromUsers.username || 'Unknown User',
      email: entry?.user_email || fromUsers.email || '',
      department: entry?.user_department || fromUsers.department || '',
      role: entry?.user_role || fromUsers.role || '',
    };
  };

  const downloadCsv = (filename, lines) => {
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.setAttribute('href', URL.createObjectURL(blob));
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportFilterLabels = () => {
    const employeeName = selectedUser
      ? (users.find((u) => u.id === selectedUser)?.full_name || users.find((u) => u.id === selectedUser)?.username || selectedUser)
      : 'All employees';
    const projectName = projectFilter
      ? (projects.find((p) => p.id === projectFilter)?.name || projectFilter)
      : 'All projects';
    const taskName = taskFilter
      ? (tasks.find((t) => t.id === taskFilter)?.name || taskFilter)
      : 'All tasks';
    return { employeeName, projectName, taskName };
  };

  // --- Export functions ---
  const exportToCSV = () => {
    if (filteredEntries.length === 0) { toast.error('No data to export'); return; }

    const { employeeName, projectName, taskName } = exportFilterLabels();
    const headers = [
      'Date',
      'Weekday',
      'Start Time (PKT)',
      'End Time (PKT)',
      'Duration (Hours)',
      'Duration (HH:MM)',
      'Project',
      'Task',
      'Task Status',
      'Description',
      'User',
      'Email',
      'Department',
      'Role',
      'Entry Type',
      'Idle (Minutes)',
      'Leave Status',
      'Entry ID',
    ];

    const lines = [
      csvRow(['Timesheet Detailed Export']),
      csvRow(['Generated At (PKT)', format(new Date(), 'yyyy-MM-dd HH:mm')]),
      csvRow(['View', viewMode === 'month' ? 'Month' : 'Week']),
      csvRow(['Report Period', `${format(periodStart, 'MMM d, yyyy')} - ${format(periodEnd, 'MMM d, yyyy')}`]),
      csvRow(['Employee Filter', employeeName]),
      csvRow(['Project Filter', projectName]),
      csvRow(['Task Filter', taskName]),
      csvRow(['Total Entries', filteredEntries.length]),
      csvRow(['Total Hours', (filteredEntries.reduce((s, e) => s + (e.duration || 0), 0) / 3600).toFixed(2)]),
      [],
      csvRow(headers),
    ];

    days.forEach((day) => {
      const dailyEntries = getEntriesForDay(day, filteredEntries);
      const dateStr = format(day, 'yyyy-MM-dd');
      const weekday = format(day, 'EEE');
      const dayLeave = leaveData[dateStr];
      const selectedUserMeta = selectedUser
        ? resolveUserMeta({ user_id: selectedUser, user_name: users.find((u) => u.id === selectedUser)?.full_name })
        : { name: 'All Users', email: '', department: '', role: '' };

      if (dayLeave?.is_on_leave) {
        lines.push(csvRow([
          dateStr,
          weekday,
          '',
          '',
          '',
          '',
          '',
          '',
          '',
          dayLeave.leave?.description || '',
          selectedUserMeta.name,
          selectedUserMeta.email,
          selectedUserMeta.department,
          selectedUserMeta.role,
          'Leave',
          '',
          `${dayLeave.leave?.category || 'Leave'} Leave`,
          dayLeave.leave?.id || '',
        ]));
      }

      if (dailyEntries.length > 0) {
        dailyEntries.forEach((entry) => {
          const meta = resolveUserMeta(entry);
          const durationSec = entry.duration || 0;
          lines.push(csvRow([
            dateStr,
            weekday,
            entry.start_time ? toUTCPlus5Time(entry.start_time) : '',
            entry.end_time ? toUTCPlus5Time(entry.end_time) : (entry.start_time ? 'Ongoing' : ''),
            (durationSec / 3600).toFixed(2),
            formatHoursHhMm(durationSec),
            entry.project_name || '',
            entry.task_name !== 'No Task' ? entry.task_name : '',
            entry.task_status || '',
            entry.description || '',
            meta.name,
            meta.email,
            meta.department,
            meta.role,
            entry.is_manual ? 'Manual' : 'Timer',
            idleMinutesForEntry(entry),
            '',
            entry.id || '',
          ]));
        });
      } else if (viewMode === 'week' && !dayLeave?.is_on_leave) {
        lines.push(csvRow([
          dateStr,
          weekday,
          '',
          '',
          '0.00',
          '0:00',
          '',
          '',
          '',
          '',
          selectedUserMeta.name,
          selectedUserMeta.email,
          selectedUserMeta.department,
          selectedUserMeta.role,
          'No entries',
          '',
          'No entries',
          '',
        ]));
      }
    });

    let filename = viewMode === 'month'
      ? `timesheet_${format(periodStart, 'yyyy-MM')}`
      : `timesheet_${format(periodStart, 'yyyy-MM-dd')}_to_${format(periodEnd, 'yyyy-MM-dd')}`;
    if (selectedUser) {
      const n = users.find((u) => u.id === selectedUser)?.full_name || 'user';
      filename += `_${n.replace(/\s+/g, '_')}`;
    }
    if (projectFilter) {
      const n = projects.find((p) => p.id === projectFilter)?.name || 'project';
      filename += `_${n.replace(/\s+/g, '_')}`;
    }
    downloadCsv(`${filename}.csv`, lines);
    toast.success(`Exported ${filteredEntries.length} entries`);
  };

  const exportSummary = () => {
    if (filteredEntries.length === 0) { toast.error('No data to export'); return; }

    const { employeeName, projectName, taskName } = exportFilterLabels();
    const totalSecondsExport = filteredEntries.reduce((sum, entry) => sum + (entry.duration || 0), 0);
    const totalHoursExport = totalSecondsExport / 3600;
    const uniqueUsers = new Set(filteredEntries.map((e) => e.user_id)).size;
    const uniqueProjects = new Set(filteredEntries.map((e) => e.project_id)).size;
    const uniqueTasks = new Set(filteredEntries.filter((e) => e.task_id).map((e) => e.task_id)).size;
    const manualCount = filteredEntries.filter((e) => e.is_manual).length;
    const timerCount = filteredEntries.length - manualCount;
    const workingDayKeys = new Set(
      filteredEntries
        .map((e) => toPKTDayKey(e.start_time))
        .filter((d) => {
          if (!d) return false;
          const dt = parseISO(d);
          const day = dt.getDay();
          return day !== 0 && day !== 6;
        })
    );
    const avgPerUser = uniqueUsers > 0 ? totalHoursExport / uniqueUsers : 0;
    const avgPerWorkingDay = workingDayKeys.size > 0 ? totalHoursExport / workingDayKeys.size : 0;

    const lines = [
      csvRow(['Timesheet Summary Report']),
      csvRow(['Generated At (PKT)', format(new Date(), 'yyyy-MM-dd HH:mm')]),
      csvRow(['View', viewMode === 'month' ? 'Month' : 'Week']),
      csvRow(['Report Period', `${format(periodStart, 'MMM d, yyyy')} - ${format(periodEnd, 'MMM d, yyyy')}`]),
      csvRow([
        viewMode === 'month' ? 'Month' : 'Week',
        viewMode === 'month'
          ? format(periodStart, 'MMMM yyyy')
          : `Week ${format(periodStart, 'w')} of ${format(periodStart, 'yyyy')}`,
      ]),
      csvRow(['Employee Filter', employeeName]),
      csvRow(['Project Filter', projectName]),
      csvRow(['Task Filter', taskName]),
      [],
      csvRow(['Overview']),
      csvRow(['Total Hours', totalHoursExport.toFixed(2)]),
      csvRow(['Total Duration (HH:MM)', formatHoursHhMm(totalSecondsExport)]),
      csvRow(['Total Entries', filteredEntries.length]),
      csvRow(['Timer Entries', timerCount]),
      csvRow(['Manual Entries', manualCount]),
      csvRow(['Total Projects', uniqueProjects]),
      csvRow(['Total Tasks', uniqueTasks]),
      csvRow(['Total Users', uniqueUsers]),
      csvRow(['Days With Entries', new Set(filteredEntries.map((e) => toPKTDayKey(e.start_time)).filter(Boolean)).size]),
      csvRow(['Working Days With Entries', workingDayKeys.size]),
      csvRow(['Avg Hours / User', avgPerUser.toFixed(2)]),
      csvRow(['Avg Hours / Working Day', avgPerWorkingDay.toFixed(2)]),
    ];

    if (selectedUser && effectivePeriodTarget > 0) {
      lines.push(csvRow(['Period Target (Hours)', effectivePeriodTarget.toFixed(2)]));
      lines.push(csvRow(['Hours vs Target', (totalHoursExport - effectivePeriodTarget).toFixed(2)]));
      lines.push(csvRow(['Target Progress %', Math.min(100, (totalHoursExport / effectivePeriodTarget) * 100).toFixed(1)]));
    }

    // Daily totals
    lines.push([]);
    lines.push(csvRow(['Daily Totals']));
    lines.push(csvRow(['Date', 'Weekday', 'Total Hours', 'Duration (HH:MM)', 'Entries', 'Users', 'Projects']));
    days.forEach((day) => {
      const dateStr = format(day, 'yyyy-MM-dd');
      const dayEntries = getEntriesForDay(day, filteredEntries);
      if (dayEntries.length === 0 && viewMode === 'month') return;
      const secs = dayEntries.reduce((s, e) => s + (e.duration || 0), 0);
      lines.push(csvRow([
        dateStr,
        format(day, 'EEE'),
        (secs / 3600).toFixed(2),
        formatHoursHhMm(secs),
        dayEntries.length,
        new Set(dayEntries.map((e) => e.user_id)).size,
        new Set(dayEntries.map((e) => e.project_id)).size,
      ]));
    });

    // Department breakdown
    const departmentBreakdown = {};
    filteredEntries.forEach((e) => {
      const meta = resolveUserMeta(e);
      const key = meta.department || 'Unassigned';
      if (!departmentBreakdown[key]) departmentBreakdown[key] = { hours: 0, count: 0, users: new Set() };
      departmentBreakdown[key].hours += (e.duration || 0) / 3600;
      departmentBreakdown[key].count += 1;
      departmentBreakdown[key].users.add(e.user_id);
    });
    lines.push([]);
    lines.push(csvRow(['Department Breakdown']));
    lines.push(csvRow(['Department', 'Total Hours', '% of Total', 'Entries', 'Users']));
    Object.entries(departmentBreakdown)
      .sort((a, b) => b[1].hours - a[1].hours)
      .forEach(([name, d]) => {
        lines.push(csvRow([
          name,
          d.hours.toFixed(2),
          totalHoursExport > 0 ? ((d.hours / totalHoursExport) * 100).toFixed(1) : '0.0',
          d.count,
          d.users.size,
        ]));
      });

    // Project breakdown
    const projectBreakdown = {};
    filteredEntries.forEach((e) => {
      const n = e.project_name || 'Unknown';
      if (!projectBreakdown[n]) {
        projectBreakdown[n] = { hours: 0, count: 0, users: new Set(), tasks: new Set() };
      }
      projectBreakdown[n].hours += (e.duration || 0) / 3600;
      projectBreakdown[n].count += 1;
      projectBreakdown[n].users.add(e.user_id);
      if (e.task_id) projectBreakdown[n].tasks.add(e.task_id);
    });
    lines.push([]);
    lines.push(csvRow(['Project Breakdown']));
    lines.push(csvRow(['Project Name', 'Total Hours', '% of Total', 'Entries', 'Users', 'Tasks']));
    Object.entries(projectBreakdown)
      .sort((a, b) => b[1].hours - a[1].hours)
      .forEach(([name, d]) => {
        lines.push(csvRow([
          name,
          d.hours.toFixed(2),
          totalHoursExport > 0 ? ((d.hours / totalHoursExport) * 100).toFixed(1) : '0.0',
          d.count,
          d.users.size,
          d.tasks.size,
        ]));
      });

    // Task breakdown
    const taskBreakdown = {};
    filteredEntries.forEach((e) => {
      const taskLabel = e.task_name && e.task_name !== 'No Task' ? e.task_name : '(No Task)';
      const key = `${e.project_name || 'Unknown'}|||${taskLabel}`;
      if (!taskBreakdown[key]) {
        taskBreakdown[key] = {
          project: e.project_name || 'Unknown',
          task: taskLabel,
          hours: 0,
          count: 0,
          users: new Set(),
        };
      }
      taskBreakdown[key].hours += (e.duration || 0) / 3600;
      taskBreakdown[key].count += 1;
      taskBreakdown[key].users.add(e.user_id);
    });
    lines.push([]);
    lines.push(csvRow(['Task Breakdown']));
    lines.push(csvRow(['Project', 'Task', 'Total Hours', '% of Total', 'Entries', 'Users']));
    Object.values(taskBreakdown)
      .sort((a, b) => b.hours - a.hours)
      .forEach((d) => {
        lines.push(csvRow([
          d.project,
          d.task,
          d.hours.toFixed(2),
          totalHoursExport > 0 ? ((d.hours / totalHoursExport) * 100).toFixed(1) : '0.0',
          d.count,
          d.users.size,
        ]));
      });

    // User breakdown
    const userBreakdown = {};
    filteredEntries.forEach((e) => {
      const meta = resolveUserMeta(e);
      const key = e.user_id || meta.name;
      if (!userBreakdown[key]) {
        userBreakdown[key] = {
          name: meta.name,
          email: meta.email,
          department: meta.department,
          role: meta.role,
          hours: 0,
          count: 0,
          projects: new Set(),
          tasks: new Set(),
          days: new Set(),
          manual: 0,
        };
      }
      userBreakdown[key].hours += (e.duration || 0) / 3600;
      userBreakdown[key].count += 1;
      userBreakdown[key].projects.add(e.project_id);
      if (e.task_id) userBreakdown[key].tasks.add(e.task_id);
      const dayKey = toPKTDayKey(e.start_time);
      if (dayKey) userBreakdown[key].days.add(dayKey);
      if (e.is_manual) userBreakdown[key].manual += 1;
    });
    lines.push([]);
    lines.push(csvRow(['User Breakdown']));
    lines.push(csvRow([
      'User Name',
      'Email',
      'Department',
      'Role',
      'Total Hours',
      '% of Total',
      'Entries',
      'Manual Entries',
      'Projects',
      'Tasks',
      'Days Worked',
      'Avg Hours / Day',
    ]));
    Object.values(userBreakdown)
      .sort((a, b) => b.hours - a.hours)
      .forEach((d) => {
        const avgDay = d.days.size > 0 ? d.hours / d.days.size : 0;
        lines.push(csvRow([
          d.name,
          d.email,
          d.department,
          d.role,
          d.hours.toFixed(2),
          totalHoursExport > 0 ? ((d.hours / totalHoursExport) * 100).toFixed(1) : '0.0',
          d.count,
          d.manual,
          d.projects.size,
          d.tasks.size,
          d.days.size,
          avgDay.toFixed(2),
        ]));
      });

    // User × Project breakdown
    const userProjectBreakdown = {};
    filteredEntries.forEach((e) => {
      const meta = resolveUserMeta(e);
      const key = `${meta.name}|||${e.project_name || 'Unknown'}`;
      if (!userProjectBreakdown[key]) {
        userProjectBreakdown[key] = {
          user: meta.name,
          email: meta.email,
          department: meta.department,
          project: e.project_name || 'Unknown',
          hours: 0,
          count: 0,
        };
      }
      userProjectBreakdown[key].hours += (e.duration || 0) / 3600;
      userProjectBreakdown[key].count += 1;
    });
    lines.push([]);
    lines.push(csvRow(['User × Project Breakdown']));
    lines.push(csvRow(['User Name', 'Email', 'Department', 'Project', 'Total Hours', '% of Total', 'Entries']));
    Object.values(userProjectBreakdown)
      .sort((a, b) => b.hours - a.hours || a.user.localeCompare(b.user))
      .forEach((d) => {
        lines.push(csvRow([
          d.user,
          d.email,
          d.department,
          d.project,
          d.hours.toFixed(2),
          totalHoursExport > 0 ? ((d.hours / totalHoursExport) * 100).toFixed(1) : '0.0',
          d.count,
        ]));
      });

    const filename = viewMode === 'month'
      ? `timesheet_summary_${format(periodStart, 'yyyy-MM')}.csv`
      : `timesheet_summary_${format(periodStart, 'yyyy-MM-dd')}_to_${format(periodEnd, 'yyyy-MM-dd')}.csv`;
    downloadCsv(filename, lines);
    toast.success('Summary report exported');
  };

  const getTotalHoursForDay = (day, entries) => {
    const dayKey = format(day, 'yyyy-MM-dd');
    return entries.filter(entry => toPKTDayKey(entry.start_time) === dayKey)
                  .reduce((sum, entry) => sum + (entry.duration || 0), 0) / 3600;
  };

  const getEntriesForDay = (day, entries) => {
    const dayKey = format(day, 'yyyy-MM-dd');
    return entries.filter(entry => toPKTDayKey(entry.start_time) === dayKey)
                  .sort((a, b) => (parseApiDate(b.start_time)?.getTime() || 0) - (parseApiDate(a.start_time)?.getTime() || 0));
  };

  const filteredEntries = timeEntries.filter(entry => {
    const matchesProject = !projectFilter || entry.project_id === projectFilter;
    const matchesTask = !taskFilter || entry.task_id === taskFilter;
    const matchesUser = !selectedUser || entry.user_id === selectedUser;
    return matchesProject && matchesTask && matchesUser;
  });

  const groupedByUser = useMemo(() => {
    if (selectedUser) return { [selectedUser]: filteredEntries };
    const grouped = {};
    filteredEntries.forEach(entry => {
      if (!grouped[entry.user_id]) grouped[entry.user_id] = [];
      grouped[entry.user_id].push(entry);
    });
    return grouped;
  }, [filteredEntries, selectedUser]);

  const uniqueUsers = useMemo(() => {
    return Object.keys(groupedByUser).map(userId => {
      const u = users.find(u => u.id === userId);
      return { id: userId, name: u?.full_name || u?.username || 'Unknown User', entries: groupedByUser[userId] || [] };
    }).sort((a, b) => a.name.localeCompare(b.name));
  }, [groupedByUser, users]);

  const totalPages = Math.ceil(uniqueUsers.length / usersPerPage);
  const startIndex = (currentPage - 1) * usersPerPage;
  const endIndex = startIndex + usersPerPage;
  const paginatedUsers = uniqueUsers.slice(startIndex, endIndex);

  const filteredEmployeesForDropdown = useMemo(() => {
    if (!employeeDropdownSearch.trim()) return users;
    const term = employeeDropdownSearch.toLowerCase().trim();
    return users.filter(u => (u.full_name || u.username || '').toLowerCase().includes(term));
  }, [users, employeeDropdownSearch]);

  const filteredProjectsForDropdown = useMemo(() => {
    if (!projectDropdownSearch.trim()) return projects;
    const term = projectDropdownSearch.toLowerCase().trim();
    return projects.filter(p => (p.name || '').toLowerCase().includes(term));
  }, [projects, projectDropdownSearch]);

  const filteredTasksForDropdown = useMemo(() => {
    if (!taskDropdownSearch.trim()) return tasks;
    const term = taskDropdownSearch.toLowerCase().trim();
    return tasks.filter(t => (t.name || '').toLowerCase().includes(term));
  }, [tasks, taskDropdownSearch]);

  useEffect(() => { setCurrentPage(1); }, [selectedUser, projectFilter, taskFilter, periodStart, periodEnd, viewMode]);

  const totalSeconds = filteredEntries.reduce((sum, e) => sum + (e.duration || 0), 0);
  const totalHours = totalSeconds / 3600;
  const projectCount = new Set(filteredEntries.map(e => e.project_id)).size;
  const taskCount = new Set(filteredEntries.filter(e => e.task_id).map(e => e.task_id)).size;
  const workingDays = days.filter(d => {
    const dateStr = format(d, 'yyyy-MM-dd');
    const isWeekend = d.getDay() === 0 || d.getDay() === 6;
    return !isWeekend && !leaveData[dateStr]?.is_on_leave && !holidayDateSet.has(dateStr);
  });
  const leaveWorkingDays = days.filter(d => {
    const dateStr = format(d, 'yyyy-MM-dd');
    return leaveData[dateStr]?.is_on_leave && d.getDay() !== 0 && d.getDay() !== 6;
  }).length;
  const holidayWorkingDays = days.filter(d => {
    const dateStr = format(d, 'yyyy-MM-dd');
    return holidayDateSet.has(dateStr) && d.getDay() !== 0 && d.getDay() !== 6;
  }).length;
  const offWorkingDays = days.filter(d => {
    const dateStr = format(d, 'yyyy-MM-dd');
    if (d.getDay() === 0 || d.getDay() === 6) return false;
    return leaveData[dateStr]?.is_on_leave || holidayDateSet.has(dateStr);
  }).length;
  const periodWorkingDayCount = days.filter(d => d.getDay() !== 0 && d.getDay() !== 6).length || (viewMode === 'month' ? 22 : 5);
  const effectivePeriodTarget = (() => {
    if (offWorkingDays >= periodWorkingDayCount) return 0;
    if (viewMode === 'month') {
      const expectedDays = periodWorkingDayCount - offWorkingDays;
      return Math.round(dailyTarget * expectedDays * 100) / 100;
    }
    return Math.round((weeklyTarget * ((periodWorkingDayCount - offWorkingDays) / periodWorkingDayCount)) * 100) / 100;
  })();
  const avgDaily = workingDays.length > 0 ? totalHours / workingDays.length : 0;
  const periodProgress =
    effectivePeriodTarget > 0
      ? Math.min((totalHours / effectivePeriodTarget) * 100, 100)
      : 100;
  const hoursShort = Math.max(0, effectivePeriodTarget - totalHours);
  const metPeriodTarget = effectivePeriodTarget <= 0 || totalHours >= effectivePeriodTarget;
  const showPeriodTargetBanner = Boolean(selectedUser) && !loading;
  const offDaysNote = (() => {
    const parts = [];
    if (leaveWorkingDays > 0) {
      parts.push(`${leaveWorkingDays} leave day${leaveWorkingDays === 1 ? '' : 's'}`);
    }
    if (holidayWorkingDays > 0) {
      parts.push(`${holidayWorkingDays} holiday${holidayWorkingDays === 1 ? '' : 's'}`);
    }
    return parts.length ? ` (${parts.join(', ')} adjusted)` : '';
  })();
  const offDaysNoteSentence = (() => {
    const parts = [];
    if (leaveWorkingDays > 0) {
      parts.push(`${leaveWorkingDays} leave day${leaveWorkingDays === 1 ? '' : 's'}`);
    }
    if (holidayWorkingDays > 0) {
      parts.push(`${holidayWorkingDays} holiday${holidayWorkingDays === 1 ? '' : 's'}`);
    }
    return parts.length ? ` Target adjusted for ${parts.join(' and ')}.` : '';
  })();

  const monthCalendarCells = useMemo(() => {
    if (viewMode !== 'month') return [];
    const startPad = (periodStart.getDay() + 6) % 7; // Mon=0 … Sun=6
    const padStart = Array.from({ length: startPad }, () => null);
    return [...padStart, ...days];
  }, [viewMode, periodStart, days]);
  const hasActiveFilters = (!isUserLocked && selectedUser) || projectFilter || taskFilter;
  const activeFilterCount = [
    !isUserLocked && selectedUser,
    projectFilter,
    taskFilter,
  ].filter(Boolean).length;

  const clearAllFilters = () => {
    if (canFilterEmployees) {
      setSelectedUser('');
    }
    setProjectFilter('');
    setTaskFilter('');
  };

  const toggleCell = (userId, dateStr) => {
    const key = `${userId}-${dateStr}`;
    setExpandedCells(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // --- Render ---

  const renderFilterDropdown = (type, label, currentValue, displayValue, onSelect, searchValue, onSearchChange, filteredItems) => (
    <div className={`w-full ${type === 'employee' ? 'lg:w-56' : 'lg:w-48'} relative filter-dropdown`}>
      <label className="text-xs font-medium text-secondary-600 dark:text-secondary-400 mb-1 block">{label}</label>
      <button
        type="button"
        onClick={() => setOpenFilterDropdown(openFilterDropdown === type ? null : type)}
        className="input-field w-full text-left flex items-center justify-between gap-2"
      >
        <span className="truncate text-sm">{displayValue || `All ${label}`}</span>
        <ChevronDown className={`h-4 w-4 flex-shrink-0 text-secondary-400 transition-transform ${openFilterDropdown === type ? 'rotate-180' : ''}`} />
      </button>
      {openFilterDropdown === type && (
        <div className="absolute z-30 mt-1 w-full bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 rounded-lg shadow-lg max-h-72 flex flex-col">
          <div className="p-2 border-b border-secondary-100 dark:border-secondary-700 sticky top-0 bg-white dark:bg-secondary-800 rounded-t-lg">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-secondary-400" />
              <input
                type="text"
                placeholder={`Search ${label.toLowerCase()}...`}
                className="input-field w-full !pl-9"
                value={searchValue}
                onChange={(e) => onSearchChange(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                autoFocus
              />
            </div>
          </div>
          <div className="overflow-y-auto flex-1 min-h-0">
            <button
              type="button"
              onClick={() => { onSelect(''); setOpenFilterDropdown(null); onSearchChange(''); }}
              className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${!currentValue ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-medium' : 'text-secondary-700 dark:text-secondary-300 hover:bg-secondary-50 dark:hover:bg-secondary-700/50'}`}
            >
              All {label}
            </button>
            {filteredItems.map(item => (
              <button
                key={item.id}
                type="button"
                onClick={() => { onSelect(item.id); setOpenFilterDropdown(null); onSearchChange(''); }}
                className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${currentValue === item.id ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-medium' : 'text-secondary-700 dark:text-secondary-300 hover:bg-secondary-50 dark:hover:bg-secondary-700/50'}`}
              >
                {item.name || item.full_name || item.username}
              </button>
            ))}
            {filteredItems.length === 0 && (
              <div className="px-4 py-3 text-sm text-secondary-500 dark:text-secondary-400">No matches</div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-5">
      {/* Filters */}
      <div className="card p-4">
        <div className="flex flex-col lg:flex-row lg:items-end gap-3">
          {canFilterEmployees && (
            renderFilterDropdown(
              'employee', 'Employee',
              selectedUser,
              selectedUser ? (users.find(u => u.id === selectedUser)?.full_name || users.find(u => u.id === selectedUser)?.username) : '',
              setSelectedUser,
              employeeDropdownSearch,
              setEmployeeDropdownSearch,
              filteredEmployeesForDropdown
            )
          )}

          {renderFilterDropdown(
            'project', 'Project',
            projectFilter,
            projectFilter ? (projects.find(p => p.id === projectFilter)?.name || '') : '',
            setProjectFilter,
            projectDropdownSearch,
            setProjectDropdownSearch,
            filteredProjectsForDropdown
          )}

          {renderFilterDropdown(
            'task', 'Task',
            taskFilter,
            taskFilter ? (tasks.find(t => t.id === taskFilter)?.name || '') : '',
            setTaskFilter,
            taskDropdownSearch,
            setTaskDropdownSearch,
            filteredTasksForDropdown
          )}

          {/* Export + Clear */}
          <div className="flex items-center gap-2 lg:ml-auto flex-shrink-0">
            {hasActiveFilters && (
              <button onClick={clearAllFilters} className="btn-secondary flex items-center gap-2 whitespace-nowrap">
                <X className="h-4 w-4" />
                Clear ({activeFilterCount})
              </button>
            )}

            <div className="relative export-dropdown">
              <button
                onClick={() => setShowExportDropdown(!showExportDropdown)}
                className="btn-secondary flex items-center gap-2 whitespace-nowrap"
                disabled={filteredEntries.length === 0}
              >
                <Download className="h-4 w-4" />
                Export
                <ChevronDown className="h-4 w-4" />
              </button>
              {showExportDropdown && (
                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 rounded-lg shadow-lg z-30">
                  <div className="py-1">
                    <button onClick={() => { exportToCSV(); setShowExportDropdown(false); }} className="w-full text-left px-4 py-2 text-sm text-secondary-700 dark:text-secondary-300 hover:bg-secondary-50 dark:hover:bg-secondary-700/50 flex items-center gap-2">
                      <FileText className="h-4 w-4" /> Detailed Timesheet
                    </button>
                    <button onClick={() => { exportSummary(); setShowExportDropdown(false); }} className="w-full text-left px-4 py-2 text-sm text-secondary-700 dark:text-secondary-300 hover:bg-secondary-50 dark:hover:bg-secondary-700/50 flex items-center gap-2">
                      <BarChart3 className="h-4 w-4" /> Summary Report
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card !p-4 flex flex-col">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 bg-primary-100 dark:bg-primary-900/40 rounded-lg flex items-center justify-center flex-shrink-0">
              <Clock className="h-4 w-4 text-primary-600 dark:text-primary-400" />
            </div>
            <div className="min-w-0">
              <p className="text-[11px] font-medium text-secondary-500 dark:text-secondary-400 leading-tight">Total Hours</p>
              <p className="text-xl font-bold text-secondary-900 dark:text-white leading-tight mt-0.5">{totalHours.toFixed(1)}h</p>
            </div>
          </div>
          <div className="mt-auto pt-3">
            <div className="flex items-center justify-between text-[11px] text-secondary-500 dark:text-secondary-400 mb-1">
              <span>{viewMode === 'month' ? 'Monthly target' : 'Weekly target'}</span>
              <span>{totalHours.toFixed(0)}/{effectivePeriodTarget}h</span>
            </div>
            <div className="h-1.5 bg-secondary-200 dark:bg-secondary-700 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${periodProgress >= 90 ? 'bg-green-500' : periodProgress >= 50 ? 'bg-primary-500' : 'bg-yellow-500'}`}
                style={{ width: `${periodProgress}%` }}
              />
            </div>
          </div>
        </div>

        <div className="card !p-4 flex flex-col">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
              <TrendingUp className="h-4 w-4 text-green-600 dark:text-green-400" />
            </div>
            <div className="min-w-0">
              <p className="text-[11px] font-medium text-secondary-500 dark:text-secondary-400 leading-tight">Avg Daily</p>
              <p className="text-xl font-bold text-secondary-900 dark:text-white leading-tight mt-0.5">{avgDaily.toFixed(1)}h</p>
            </div>
          </div>
          <p className="text-[11px] text-secondary-400 dark:text-secondary-500 mt-auto pt-3">{workingDays.length} working day{workingDays.length !== 1 ? 's' : ''}</p>
        </div>

        <div className="card !p-4 flex flex-col">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 bg-primary-100 dark:bg-primary-900/40 rounded-lg flex items-center justify-center flex-shrink-0">
              <FolderOpen className="h-4 w-4 text-primary-600 dark:text-primary-400" />
            </div>
            <div className="min-w-0">
              <p className="text-[11px] font-medium text-secondary-500 dark:text-secondary-400 leading-tight">Projects</p>
              <p className="text-xl font-bold text-secondary-900 dark:text-white leading-tight mt-0.5">{projectCount}</p>
            </div>
          </div>
        </div>

        <div className="card !p-4 flex flex-col">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 bg-secondary-100 dark:bg-secondary-700 rounded-lg flex items-center justify-center flex-shrink-0">
              <CheckSquare className="h-4 w-4 text-secondary-600 dark:text-secondary-400" />
            </div>
            <div className="min-w-0">
              <p className="text-[11px] font-medium text-secondary-500 dark:text-secondary-400 leading-tight">Tasks</p>
              <p className="text-xl font-bold text-secondary-900 dark:text-white leading-tight mt-0.5">{taskCount}</p>
            </div>
          </div>
        </div>
      </div>

      {showPeriodTargetBanner && (
        <div
          className={`rounded-xl border px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3 ${
            metPeriodTarget
              ? 'border-emerald-200 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-900/20'
              : 'border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20'
          }`}
        >
          <div
            className={`h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
              metPeriodTarget
                ? 'bg-emerald-100 dark:bg-emerald-900/40'
                : 'bg-amber-100 dark:bg-amber-900/40'
            }`}
          >
            {metPeriodTarget ? (
              <TrendingUp className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
            ) : (
              <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p
              className={`text-sm font-semibold ${
                metPeriodTarget
                  ? 'text-emerald-800 dark:text-emerald-200'
                  : 'text-amber-800 dark:text-amber-200'
              }`}
            >
              {metPeriodTarget
                ? `${viewMode === 'month' ? 'Monthly' : 'Weekly'} target met — ${totalHours.toFixed(1)}h logged`
                : `Under ${periodLabel}ly target — ${hoursShort.toFixed(1)}h short of ${effectivePeriodTarget}h`}
            </p>
            <p
              className={`text-xs mt-0.5 ${
                metPeriodTarget
                  ? 'text-emerald-700/80 dark:text-emerald-300/80'
                  : 'text-amber-700/80 dark:text-amber-300/80'
              }`}
            >
              {metPeriodTarget
                ? `This user has clocked at least ${effectivePeriodTarget} hours for the selected ${periodLabel}${offDaysNote}.`
                : `Logged ${totalHours.toFixed(1)}h this ${periodLabel}. Remaining to hit ${effectivePeriodTarget}h: ${hoursShort.toFixed(1)}h.${offDaysNoteSentence}`}
            </p>
          </div>
          <div className="sm:w-40 flex-shrink-0">
            <div className="flex items-center justify-between text-[11px] mb-1 text-secondary-600 dark:text-secondary-300">
              <span>Progress</span>
              <span>{Math.round(periodProgress)}%</span>
            </div>
            <div className="h-1.5 bg-white/70 dark:bg-secondary-800/70 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${
                  metPeriodTarget ? 'bg-emerald-500' : periodProgress >= 50 ? 'bg-amber-500' : 'bg-red-500'
                }`}
                style={{ width: `${periodProgress}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Period Navigation */}
      <div className="card !p-0 overflow-hidden">
        <div className="px-4 pt-2">
          <ViewTabs
            tabs={VIEW_TABS}
            activeId={viewMode}
            onChange={handleViewModeChange}
          />
        </div>
        <div className="flex items-center justify-between px-3 py-3">
          <button onClick={goToPreviousPeriod} className="flex items-center gap-1.5 text-sm text-secondary-600 dark:text-secondary-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors px-2 py-1 rounded-lg hover:bg-secondary-50 dark:hover:bg-secondary-700/50">
            <ChevronLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Previous</span>
          </button>

          <div className="flex items-center gap-3">
            <div className="text-center">
              <h2 className="text-sm sm:text-base font-semibold text-secondary-900 dark:text-white">
                {viewMode === 'month'
                  ? format(periodStart, 'MMMM yyyy')
                  : `${format(periodStart, 'MMM d')} – ${format(periodEnd, 'MMM d, yyyy')}`}
              </h2>
              <p className="text-xs text-secondary-500 dark:text-secondary-400">
                {viewMode === 'month'
                  ? `${format(periodStart, 'MMM d')} – ${format(periodEnd, 'MMM d, yyyy')}`
                  : `Week ${format(periodStart, 'w')} of ${format(periodStart, 'yyyy')}`}
              </p>
            </div>
            <div className="flex items-center gap-1.5">
              <label className="cursor-pointer p-1.5 rounded-lg hover:bg-secondary-50 dark:hover:bg-secondary-700/50 transition-colors" title="Jump to date">
                <CalendarDays className="h-4 w-4 text-secondary-400 dark:text-secondary-500" />
                <input type="date" className="sr-only" onChange={(e) => jumpToDate(e.target.value)} />
              </label>
              <button onClick={goToToday} className="text-xs px-2 py-1 rounded-md bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 font-medium hover:bg-primary-100 dark:hover:bg-primary-900/50 transition-colors">
                Today
              </button>
            </div>
          </div>

          <button onClick={goToNextPeriod} className="flex items-center gap-1.5 text-sm text-secondary-600 dark:text-secondary-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors px-2 py-1 rounded-lg hover:bg-secondary-50 dark:hover:bg-secondary-700/50">
            <span className="hidden sm:inline">Next</span>
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
      {/* Period Grid */}
      {loading ? (
        <div className="card !p-0 overflow-hidden">
          <div className="animate-pulse">
            <div className="h-14 bg-secondary-100 dark:bg-secondary-700" />
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="flex border-t border-secondary-100 dark:border-secondary-700/50">
                <div className="w-64 p-3 space-y-2">
                  <div className="h-4 bg-secondary-200 dark:bg-secondary-700 rounded w-40" />
                  <div className="h-3 bg-secondary-200 dark:bg-secondary-700 rounded w-28" />
                </div>
                {[1, 2, 3, 4, 5, 6, 7].map(j => (
                  <div key={j} className="flex-1 p-3 border-l border-secondary-100 dark:border-secondary-700/50">
                    <div className="h-4 bg-secondary-200 dark:bg-secondary-700 rounded w-12 mx-auto" />
                  </div>
                ))}
                <div className="w-24 p-3 border-l border-secondary-100 dark:border-secondary-700/50">
                  <div className="h-4 bg-secondary-200 dark:bg-secondary-700 rounded w-14 mx-auto" />
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : filteredEntries.length === 0 && viewMode === 'week' ? (
        <div className="card text-center py-12">
          <Clock className="h-10 w-10 text-secondary-300 dark:text-secondary-600 mx-auto mb-3" />
          <h3 className="text-base font-medium text-secondary-900 dark:text-white mb-1">No time entries found</h3>
          <p className="text-sm text-secondary-500 dark:text-secondary-400 max-w-sm mx-auto">
            No entries for the selected {periodLabel} and filters. Try adjusting the date range or filters.
          </p>
        </div>
      ) : viewMode === 'month' ? (
        <>
          <div className="card !p-4" data-testid="timesheet-month-calendar">
            <div className="grid grid-cols-7 gap-1 mb-2">
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((label) => (
                <div key={label} className="text-center text-[11px] font-semibold text-secondary-500 dark:text-secondary-400 py-1">
                  {label}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {monthCalendarCells.map((day, idx) => {
                if (!day) {
                  return <div key={`pad-${idx}`} className="min-h-[4.5rem] rounded-lg bg-transparent" />;
                }
                const dateStr = format(day, 'yyyy-MM-dd');
                const dayHours = getTotalHoursForDay(day, filteredEntries);
                const isOnLeave = leaveData[dateStr]?.is_on_leave;
                const isHoliday = holidayDateSet.has(dateStr);
                const weekend = day.getDay() === 0 || day.getDay() === 6;
                return (
                  <div
                    key={dateStr}
                    className={`min-h-[4.5rem] rounded-lg border p-1.5 flex flex-col ${
                      isToday(day)
                        ? 'border-primary-300 bg-primary-50/70 dark:border-primary-700 dark:bg-primary-900/20'
                        : isHoliday
                          ? 'border-violet-200 bg-violet-50/70 dark:border-violet-800 dark:bg-violet-900/15'
                          : weekend
                            ? 'border-secondary-100 bg-secondary-50/40 dark:border-secondary-800 dark:bg-secondary-900/20'
                            : 'border-secondary-200 bg-white dark:border-secondary-700 dark:bg-secondary-800/40'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-1">
                      <span className={`text-xs font-semibold ${isToday(day) ? 'text-primary-600' : 'text-secondary-700 dark:text-secondary-200'}`}>
                        {format(day, 'd')}
                      </span>
                      {isOnLeave && <Umbrella className="h-3 w-3 text-orange-500" />}
                      {isHoliday && !isOnLeave && (
                        <span title={holidayNameByDate[dateStr] || 'Holiday'}>
                          <CalendarDays className="h-3 w-3 text-violet-500" />
                        </span>
                      )}
                    </div>
                    <div className="mt-auto text-[11px] font-semibold text-secondary-800 dark:text-secondary-200">
                      {dayHours > 0 ? `${dayHours.toFixed(1)}h` : (isOnLeave || isHoliday ? '' : '—')}
                    </div>
                    {isHoliday && (
                      <div className="text-[9px] text-violet-600 dark:text-violet-400 truncate" title={holidayNameByDate[dateStr]}>
                        {holidayNameByDate[dateStr]}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {filteredEntries.length === 0 ? (
            <div className="card text-center py-12">
              <Clock className="h-10 w-10 text-secondary-300 dark:text-secondary-600 mx-auto mb-3" />
              <h3 className="text-base font-medium text-secondary-900 dark:text-white mb-1">No time entries found</h3>
              <p className="text-sm text-secondary-500 dark:text-secondary-400 max-w-sm mx-auto">
                No entries for the selected month and filters. Try adjusting the date range or filters.
              </p>
            </div>
          ) : (
            <>
              {!selectedUser && uniqueUsers.length > usersPerPage && (
                <div className="flex items-center justify-between">
                  <p className="text-xs text-secondary-500 dark:text-secondary-400">
                    Showing {startIndex + 1}–{Math.min(endIndex, uniqueUsers.length)} of {uniqueUsers.length} members
                  </p>
                  <div className="flex items-center gap-1">
                    <button onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} className="p-1.5 rounded-lg border border-secondary-200 dark:border-secondary-700 hover:bg-secondary-50 dark:hover:bg-secondary-700/50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                      <ChevronLeft className="h-3.5 w-3.5 text-secondary-600 dark:text-secondary-400" />
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                      <button key={page} onClick={() => setCurrentPage(page)} className={`px-2.5 py-1 text-xs font-medium rounded-lg transition-colors ${currentPage === page ? 'bg-primary-600 text-white' : 'text-secondary-600 dark:text-secondary-400 hover:bg-secondary-50 dark:hover:bg-secondary-700/50'}`}>
                        {page}
                      </button>
                    ))}
                    <button onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages} className="p-1.5 rounded-lg border border-secondary-200 dark:border-secondary-700 hover:bg-secondary-50 dark:hover:bg-secondary-700/50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                      <ChevronRight className="h-3.5 w-3.5 text-secondary-600 dark:text-secondary-400" />
                    </button>
                  </div>
                </div>
              )}

              {paginatedUsers.map(userData => {
                const taskGroupMap = {};
                userData.entries.forEach(entry => {
                  const key = entry.task_id || `notask-${entry.project_id}`;
                  if (!taskGroupMap[key]) {
                    taskGroupMap[key] = {
                      key,
                      taskName: entry.task_name !== "No Task" ? entry.task_name : null,
                      projectName: entry.project_name || 'Unknown Project',
                      projectId: entry.project_id,
                      taskId: entry.task_id,
                      entries: []
                    };
                  }
                  taskGroupMap[key].entries.push(entry);
                });
                const taskRows = Object.values(taskGroupMap).sort((a, b) => {
                  const aTotal = a.entries.reduce((s, e) => s + (e.duration || 0), 0);
                  const bTotal = b.entries.reduce((s, e) => s + (e.duration || 0), 0);
                  return bTotal - aTotal;
                });
                const userTotalSeconds = userData.entries.reduce((sum, e) => sum + (e.duration || 0), 0);

                return (
                  <div key={userData.id} className="card !p-0 overflow-hidden">
                    {!selectedUser && (
                      <div className="flex items-center justify-between px-4 py-2.5 border-b border-secondary-200 dark:border-secondary-700 bg-secondary-50 dark:bg-secondary-800/50">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className="h-7 w-7 rounded-full bg-primary-100 dark:bg-primary-900/40 flex items-center justify-center flex-shrink-0">
                            <User className="h-3.5 w-3.5 text-primary-600 dark:text-primary-400" />
                          </div>
                          <span className="text-sm font-semibold text-secondary-900 dark:text-white truncate">{userData.name}</span>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-secondary-500 dark:text-secondary-400 flex-shrink-0">
                          <span>{userData.entries.length} entries</span>
                          <span className="font-semibold text-primary-600 dark:text-primary-400">{formatDuration(userTotalSeconds)}</span>
                        </div>
                      </div>
                    )}
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[520px]">
                        <thead>
                          <tr className="border-b border-secondary-200 dark:border-secondary-700">
                            <th className="text-left px-4 py-3 text-[11px] font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
                              Task / Project
                            </th>
                            <th className="text-center px-3 py-3 text-[11px] font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider w-28">
                              Entries
                            </th>
                            <th className="text-center px-3 py-3 text-[11px] font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider w-32 bg-secondary-50/80 dark:bg-secondary-800/60">
                              Total
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-secondary-100 dark:divide-secondary-700/50">
                          {taskRows.map((taskRow) => {
                            const rowTotalSeconds = taskRow.entries.reduce((s, e) => s + (e.duration || 0), 0);
                            const isExpanded = expandedCells[`${userData.id}-${taskRow.key}`];
                            return (
                              <React.Fragment key={taskRow.key}>
                                <tr
                                  onClick={() => toggleCell(userData.id, taskRow.key)}
                                  className="hover:bg-secondary-50/60 dark:hover:bg-secondary-700/20 cursor-pointer transition-colors"
                                >
                                  <td className="px-4 py-3">
                                    <div className="text-sm font-medium leading-snug truncate">
                                      {taskRow.taskId && taskRow.taskName ? (
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            navigate(`/tasks/${taskRow.taskId}`);
                                          }}
                                          className="text-left text-secondary-900 dark:text-white hover:text-primary-600 dark:hover:text-primary-400 hover:underline truncate max-w-full"
                                        >
                                          {taskRow.taskName}
                                        </button>
                                      ) : (
                                        <span className="text-secondary-900 dark:text-white">
                                          {taskRow.taskName || taskRow.entries[0]?.description || 'General Work'}
                                        </span>
                                      )}
                                    </div>
                                    <div className="text-xs text-secondary-500 dark:text-secondary-400 mt-1 truncate">
                                      {taskRow.projectName}
                                    </div>
                                  </td>
                                  <td className="text-center px-3 py-3 text-sm text-secondary-600 dark:text-secondary-300">
                                    {taskRow.entries.length}
                                  </td>
                                  <td className="text-center px-3 py-3 bg-secondary-50/50 dark:bg-secondary-800/30">
                                    <span className="text-sm font-semibold text-secondary-900 dark:text-white">
                                      {formatDuration(rowTotalSeconds)}
                                    </span>
                                  </td>
                                </tr>
                                {isExpanded && (
                                  <tr className="bg-secondary-50/50 dark:bg-secondary-800/30">
                                    <td colSpan={3} className="px-4 py-3">
                                      <div className="space-y-1.5">
                                        {taskRow.entries
                                          .sort((a, b) => (parseApiDate(b.start_time)?.getTime() || 0) - (parseApiDate(a.start_time)?.getTime() || 0))
                                          .map(entry => (
                                            <div key={entry.id} className="flex items-center gap-4 text-xs bg-white dark:bg-secondary-800 rounded-lg p-3 border border-secondary-200 dark:border-secondary-700">
                                              <span className="text-secondary-500 dark:text-secondary-400 w-28 flex-shrink-0">
                                                {formatPKTDayLabel(entry.start_time)}
                                              </span>
                                              <span className="text-secondary-500 dark:text-secondary-400 w-36 flex-shrink-0">
                                                {toUTCPlus5Time(entry.start_time)} – {entry.end_time ? toUTCPlus5Time(entry.end_time) : 'Ongoing'}
                                              </span>
                                              <span className="flex-1 text-secondary-700 dark:text-secondary-300 truncate">
                                                {entry.description || '—'}
                                              </span>
                                              <span className="font-semibold text-primary-600 dark:text-primary-400 flex-shrink-0">
                                                {formatDuration(entry.duration || 0)}
                                              </span>
                                            </div>
                                          ))}
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}

              <div className="card !py-3 !px-4">
                <div className="flex items-center justify-between text-xs text-secondary-500 dark:text-secondary-400">
                  <div>
                    {selectedUser ? (
                      <span><span className="font-medium text-secondary-700 dark:text-secondary-300">{filteredEntries.length}</span> entries{filteredEntries.length !== timeEntries.length && <span className="ml-1">(filtered from {timeEntries.length})</span>}</span>
                    ) : (
                      <span><span className="font-medium text-secondary-700 dark:text-secondary-300">{uniqueUsers.length}</span> members, <span className="font-medium text-secondary-700 dark:text-secondary-300">{filteredEntries.length}</span> entries</span>
                    )}
                  </div>
                  <div>
                    Total: <span className="font-semibold text-primary-600 dark:text-primary-400">{formatDuration(totalSeconds)}</span>
                  </div>
                </div>
              </div>
            </>
          )}
        </>
      ) : (
        <>
          {/* Pagination */}
          {!selectedUser && uniqueUsers.length > usersPerPage && (
            <div className="flex items-center justify-between">
              <p className="text-xs text-secondary-500 dark:text-secondary-400">
                Showing {startIndex + 1}–{Math.min(endIndex, uniqueUsers.length)} of {uniqueUsers.length} members
              </p>
              <div className="flex items-center gap-1">
                <button onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} className="p-1.5 rounded-lg border border-secondary-200 dark:border-secondary-700 hover:bg-secondary-50 dark:hover:bg-secondary-700/50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                  <ChevronLeft className="h-3.5 w-3.5 text-secondary-600 dark:text-secondary-400" />
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <button key={page} onClick={() => setCurrentPage(page)} className={`px-2.5 py-1 text-xs font-medium rounded-lg transition-colors ${currentPage === page ? 'bg-primary-600 text-white' : 'text-secondary-600 dark:text-secondary-400 hover:bg-secondary-50 dark:hover:bg-secondary-700/50'}`}>
                    {page}
                  </button>
                ))}
                <button onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages} className="p-1.5 rounded-lg border border-secondary-200 dark:border-secondary-700 hover:bg-secondary-50 dark:hover:bg-secondary-700/50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                  <ChevronRight className="h-3.5 w-3.5 text-secondary-600 dark:text-secondary-400" />
                </button>
              </div>
            </div>
          )}

          {/* Task-per-row table (per user) */}
          {paginatedUsers.map(userData => {
            const taskGroupMap = {};
            userData.entries.forEach(entry => {
              const key = entry.task_id || `notask-${entry.project_id}`;
              if (!taskGroupMap[key]) {
                taskGroupMap[key] = {
                  key,
                  taskName: entry.task_name !== "No Task" ? entry.task_name : null,
                  projectName: entry.project_name || 'Unknown Project',
                  projectId: entry.project_id,
                  taskId: entry.task_id,
                  entries: []
                };
              }
              taskGroupMap[key].entries.push(entry);
            });
            const taskRows = Object.values(taskGroupMap).sort((a, b) => {
              const aTotal = a.entries.reduce((s, e) => s + (e.duration || 0), 0);
              const bTotal = b.entries.reduce((s, e) => s + (e.duration || 0), 0);
              return bTotal - aTotal;
            });
            const userTotalSeconds = userData.entries.reduce((sum, e) => sum + (e.duration || 0), 0);

            return (
              <div key={userData.id} className="card !p-0 overflow-hidden">
                {/* User header (admin multi-user view) */}
                {!selectedUser && (
                  <div className="flex items-center justify-between px-4 py-2.5 border-b border-secondary-200 dark:border-secondary-700 bg-secondary-50 dark:bg-secondary-800/50">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="h-7 w-7 rounded-full bg-primary-100 dark:bg-primary-900/40 flex items-center justify-center flex-shrink-0">
                        <User className="h-3.5 w-3.5 text-primary-600 dark:text-primary-400" />
                      </div>
                      <span className="text-sm font-semibold text-secondary-900 dark:text-white truncate">{userData.name}</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-secondary-500 dark:text-secondary-400 flex-shrink-0">
                      <span>{userData.entries.length} entries</span>
                      <span className="font-semibold text-primary-600 dark:text-primary-400">{formatDuration(userTotalSeconds)}</span>
                    </div>
                  </div>
                )}

                {/* Mobile: per-task cards with day chips */}
                <div className="md:hidden divide-y divide-secondary-100 dark:divide-secondary-700">
                  {taskRows.map((taskRow) => {
                    const rowTotalSeconds = taskRow.entries.reduce((s, e) => s + (e.duration || 0), 0);
                    const isExpanded = expandedCells[`${userData.id}-${taskRow.key}`];
                    return (
                      <div key={taskRow.key} className="p-3 space-y-3">
                        <button
                          type="button"
                          onClick={() => toggleCell(userData.id, taskRow.key)}
                          className="w-full text-left space-y-1"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0 flex-1">
                              {taskRow.taskId && taskRow.taskName ? (
                                <span
                                  role="link"
                                  tabIndex={0}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/tasks/${taskRow.taskId}`);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      e.stopPropagation();
                                      navigate(`/tasks/${taskRow.taskId}`);
                                    }
                                  }}
                                  className="text-sm font-medium text-secondary-900 dark:text-white hover:text-primary-600"
                                >
                                  {taskRow.taskName}
                                </span>
                              ) : (
                                <span className="text-sm font-medium text-secondary-900 dark:text-white">
                                  {taskRow.taskName || taskRow.entries[0]?.description || 'General Work'}
                                </span>
                              )}
                              <div className="text-xs text-secondary-500 truncate mt-0.5">
                                {taskRow.projectName}
                              </div>
                            </div>
                            <span className="shrink-0 text-sm font-semibold text-primary-600">
                              {formatDuration(rowTotalSeconds)}
                            </span>
                          </div>
                        </button>
                        <div className="grid grid-cols-7 gap-1">
                          {days.map((day) => {
                            const dateStr = format(day, 'yyyy-MM-dd');
                            const dayLeave = leaveData[dateStr];
                            const dayEntries = taskRow.entries.filter((e) => toPKTDayKey(e.start_time) === dateStr);
                            const daySeconds = dayEntries.reduce((s, e) => s + (e.duration || 0), 0);
                            const isOnLeave = dayLeave?.is_on_leave;
                            const isHoliday = holidayDateSet.has(dateStr);
                            return (
                              <div
                                key={dateStr}
                                className={`rounded-lg p-1.5 text-center ${isToday(day) ? 'bg-primary-50 dark:bg-primary-900/20 ring-1 ring-primary-200' : isHoliday ? 'bg-violet-50 dark:bg-violet-900/20' : 'bg-secondary-50 dark:bg-secondary-800/50'}`}
                              >
                                <div className="text-[10px] font-medium text-secondary-500">{format(day, 'EEE')}</div>
                                <div className="text-[10px] text-secondary-400">{format(day, 'd')}</div>
                                <div className="mt-0.5 text-[10px] font-semibold text-secondary-800 dark:text-secondary-200 min-h-[1rem]">
                                  {isOnLeave ? (
                                    <Umbrella className="h-3 w-3 text-orange-500 mx-auto" />
                                  ) : isHoliday ? (
                                    <CalendarDays className="h-3 w-3 text-violet-500 mx-auto" title={holidayNameByDate[dateStr] || 'Holiday'} />
                                  ) : daySeconds > 0 ? (
                                    formatDuration(daySeconds)
                                  ) : (
                                    '—'
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        {isExpanded && (
                          <div className="space-y-1.5 pt-1">
                            {taskRow.entries
                              .sort((a, b) => (parseApiDate(b.start_time)?.getTime() || 0) - (parseApiDate(a.start_time)?.getTime() || 0))
                              .map((entry) => (
                                <div
                                  key={entry.id}
                                  className="text-xs bg-secondary-50 dark:bg-secondary-800 rounded-lg p-3 border border-secondary-200 dark:border-secondary-700 space-y-1"
                                >
                                  <div className="flex justify-between gap-2">
                                    <span className="text-secondary-500">{formatPKTDayLabel(entry.start_time)}</span>
                                    <span className="font-semibold text-primary-600">{formatDuration(entry.duration || 0)}</span>
                                  </div>
                                  <div className="text-secondary-500">
                                    {toUTCPlus5Time(entry.start_time)} – {entry.end_time ? toUTCPlus5Time(entry.end_time) : 'Ongoing'}
                                  </div>
                                  <div className="text-secondary-600 dark:text-secondary-400 break-words">
                                    {entry.description || '—'}
                                  </div>
                                </div>
                              ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full min-w-[800px]">
                    <thead>
                      <tr className="border-b border-secondary-200 dark:border-secondary-700">
                        <th className="text-left px-4 py-3 text-[11px] font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider w-[280px] bg-white dark:bg-secondary-800">
                          Task / Location
                        </th>
                        {days.map(day => {
                          const dayTotal = getTotalHoursForDay(day, userData.entries);
                          const dateStr = format(day, 'yyyy-MM-dd');
                          const isHoliday = holidayDateSet.has(dateStr);
                          return (
                            <th key={day.toISOString()} className={`text-center px-2 py-3 ${isToday(day) ? 'bg-primary-50/60 dark:bg-primary-900/15' : isHoliday ? 'bg-violet-50/80 dark:bg-violet-900/15' : 'bg-white dark:bg-secondary-800'}`}>
                              <div className={`text-[11px] font-medium ${isToday(day) ? 'text-primary-600 dark:text-primary-400' : isHoliday ? 'text-violet-700 dark:text-violet-300' : 'text-secondary-500 dark:text-secondary-400'}`}>
                                {format(day, 'EEE, MMM d')}
                              </div>
                              {isHoliday && (
                                <div className="text-[9px] font-semibold uppercase tracking-wide text-violet-600 dark:text-violet-400 mt-0.5 truncate max-w-[5.5rem] mx-auto" title={holidayNameByDate[dateStr] || 'Holiday'}>
                                  {holidayNameByDate[dateStr] || 'Holiday'}
                                </div>
                              )}
                              <div className={`text-sm font-bold mt-0.5 ${dayTotal > 0 ? 'text-secondary-900 dark:text-white' : 'text-secondary-300 dark:text-secondary-600'}`}>
                                {dayTotal > 0 ? formatDuration(dayTotal * 3600) : '0h'}
                              </div>
                            </th>
                          );
                        })}
                        <th className="text-center px-3 py-3 bg-secondary-50/80 dark:bg-secondary-800/60">
                          <div className="text-[11px] font-semibold text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">Total</div>
                          <div className="text-sm font-bold text-secondary-900 dark:text-white mt-0.5">
                            {formatDuration(userTotalSeconds)}
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-secondary-100 dark:divide-secondary-700/50">
                      {taskRows.map((taskRow) => {
                        const rowTotalSeconds = taskRow.entries.reduce((s, e) => s + (e.duration || 0), 0);
                        const isExpanded = expandedCells[`${userData.id}-${taskRow.key}`];
                        return (
                          <React.Fragment key={taskRow.key}>
                            <tr
                              onClick={() => toggleCell(userData.id, taskRow.key)}
                              className="hover:bg-secondary-50/60 dark:hover:bg-secondary-700/20 cursor-pointer transition-colors"
                            >
                              <td className="px-4 py-3 w-[280px]">
                                <div className="text-sm font-medium leading-snug truncate">
                                  {taskRow.taskId && taskRow.taskName ? (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(`/tasks/${taskRow.taskId}`);
                                      }}
                                      className="text-left text-secondary-900 dark:text-white hover:text-primary-600 dark:hover:text-primary-400 hover:underline truncate max-w-full"
                                      title={`Open task: ${taskRow.taskName}`}
                                    >
                                      {taskRow.taskName}
                                    </button>
                                  ) : (
                                    <span className="text-secondary-900 dark:text-white">
                                      {taskRow.taskName || taskRow.entries[0]?.description || 'General Work'}
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-1.5 mt-1">
                                  <span className="inline-block w-2 h-2 rounded-full bg-primary-500 dark:bg-primary-400 flex-shrink-0" />
                                  {taskRow.projectId ? (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(`/projects/${taskRow.projectId}`);
                                      }}
                                      className="text-xs text-secondary-500 dark:text-secondary-400 hover:text-primary-600 dark:hover:text-primary-400 hover:underline truncate text-left max-w-full"
                                      title={`Open project: ${taskRow.projectName}`}
                                    >
                                      {taskRow.projectName}
                                    </button>
                                  ) : (
                                    <span className="text-xs text-secondary-500 dark:text-secondary-400 truncate">
                                      {taskRow.projectName}
                                    </span>
                                  )}
                                </div>
                              </td>
                              {days.map(day => {
                                const dateStr = format(day, 'yyyy-MM-dd');
                                const dayLeave = leaveData[dateStr];
                                const dayEntries = taskRow.entries.filter(e => toPKTDayKey(e.start_time) === dateStr);
                                const daySeconds = dayEntries.reduce((s, e) => s + (e.duration || 0), 0);
                                const isOnLeave = dayLeave?.is_on_leave;
                                const isHoliday = holidayDateSet.has(dateStr);
                                return (
                                  <td key={dateStr} className={`text-center px-2 py-3 ${isToday(day) ? 'bg-primary-50/30 dark:bg-primary-900/5' : isHoliday ? 'bg-violet-50/40 dark:bg-violet-900/10' : ''}`}>
                                    {isOnLeave ? (
                                      <div className="flex items-center justify-center gap-1">
                                        <Umbrella className="h-3.5 w-3.5 text-orange-500 dark:text-orange-400" />
                                      </div>
                                    ) : isHoliday ? (
                                      <div className="flex items-center justify-center gap-1" title={holidayNameByDate[dateStr] || 'Holiday'}>
                                        <CalendarDays className="h-3.5 w-3.5 text-violet-500 dark:text-violet-400" />
                                      </div>
                                    ) : daySeconds > 0 ? (
                                      <span className="text-sm font-medium text-secondary-700 dark:text-secondary-200">
                                        {formatDuration(daySeconds)}
                                      </span>
                                    ) : (
                                      <span className="text-secondary-300 dark:text-secondary-600">—</span>
                                    )}
                                  </td>
                                );
                              })}
                              <td className="text-center px-3 py-3 bg-secondary-50/50 dark:bg-secondary-800/30">
                                <span className="text-sm font-semibold text-secondary-900 dark:text-white">
                                  {formatDuration(rowTotalSeconds)}
                                </span>
                              </td>
                            </tr>

                            {isExpanded && (
                              <tr className="bg-secondary-50/50 dark:bg-secondary-800/30">
                                <td colSpan={days.length + 2} className="px-4 py-3">
                                  <div className="space-y-1.5">
                                    {taskRow.entries
                                      .sort((a, b) => (parseApiDate(b.start_time)?.getTime() || 0) - (parseApiDate(a.start_time)?.getTime() || 0))
                                      .map(entry => (
                                        <div key={entry.id} className="flex items-center gap-4 text-xs bg-white dark:bg-secondary-800 rounded-lg p-3 border border-secondary-200 dark:border-secondary-700">
                                          <span className="text-secondary-500 dark:text-secondary-400 w-24 flex-shrink-0">
                                            {formatPKTDayLabel(entry.start_time)}
                                          </span>
                                          <span className="text-secondary-500 dark:text-secondary-400 w-36 flex-shrink-0">
                                            {toUTCPlus5Time(entry.start_time)} – {entry.end_time ? toUTCPlus5Time(entry.end_time) : 'Ongoing'}
                                          </span>
                                          <span className="font-semibold text-primary-600 dark:text-primary-400 w-16 flex-shrink-0">
                                            {formatDuration(entry.duration || 0)}
                                          </span>
                                          <span className="text-secondary-600 dark:text-secondary-400 truncate flex-1">
                                            {entry.description || '—'}
                                          </span>
                                          {entry.is_manual && (
                                            <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-secondary-100 dark:bg-secondary-700 text-secondary-500 dark:text-secondary-400 flex-shrink-0">Manual</span>
                                          )}
                                        </div>
                                      ))}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}

          {/* Footer summary */}
          <div className="card !py-3 !px-4">
            <div className="flex items-center justify-between text-xs text-secondary-500 dark:text-secondary-400">
              <div>
                {selectedUser ? (
                  <span><span className="font-medium text-secondary-700 dark:text-secondary-300">{filteredEntries.length}</span> entries{filteredEntries.length !== timeEntries.length && <span className="ml-1">(filtered from {timeEntries.length})</span>}</span>
                ) : (
                  <span><span className="font-medium text-secondary-700 dark:text-secondary-300">{uniqueUsers.length}</span> members, <span className="font-medium text-secondary-700 dark:text-secondary-300">{filteredEntries.length}</span> entries</span>
                )}
              </div>
              <div>
                Total: <span className="font-semibold text-primary-600 dark:text-primary-400">{formatDuration(totalSeconds)}</span>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Timesheet;
